import { useContext, useRef, useState } from "react";
import { Box, Button, Collapse, Divider, Fade, IconButton, Paper, Typography, Unstable_Grid2 as Grid } from "@mui/material";
import { FilterAlt, HistoryToggleOff, KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";
import type { ReactNode } from "react";
import { Timeline, timelineItemClasses } from "@mui/lab";
import { RecordContext } from "../../../components/record/Record";
import { useUser } from "../../../api/useAuth";
import { getLogbookNoteFilter, canSeeLogbookDetails, canSeeLogbookOrders } from "./logbookFeature";
import { LogbookSessionItem } from "./components/LogbookSessionItem";
import { LogbookNoteItemComponent } from "./components/LogbookNoteItem";
import { LogbookPrescriptionItem } from "./components/LogbookPrescriptionItem";
import { LogbookOrderItem } from "./components/LogbookOrderItem";
import { LogbookTaskItemComponent } from "./components/LogbookTaskItem";
import { LogbookCustomerSurveyItemComponent } from "./components/LogbookCustomerSurveyItem";
import { LogbookDetailPanel } from "./components/LogbookDetailPanel";
import { LogbookServiceFilter } from "./filters/LogbookServiceFilter";
import { LogbookStatusFilter } from "./filters/LogbookStatusFilter";
import { LogbookTaskFilter } from "./filters/LogbookTaskFilter";
import { LogbookOrdersFilter } from "./filters/LogbookOrdersFilter";
import { getEntryMonth, getEntryId } from "./buildTimeline";
import { useLogbookData } from "./useLogbookData";
import { useScrollPosition } from "./useScrollPosition";
import type { LogbookEntry } from "../types";

export const Logbook = () => {
	const timelineScrollRef = useRef<HTMLDivElement>(null);
	const { isAtTop, isAtBottom, scrollToTop, scrollToBottom } = useScrollPosition(timelineScrollRef);

	const recordContext = useContext(RecordContext);
	const { data: user } = useUser();
	const treatmentId = recordContext?.item?.id;
	const sessions = recordContext?.item?.sessions ?? [];
	const treatmentClientOwnerId = recordContext?.item?.client_owner?.user_id?.id;
	const noteFilter = getLogbookNoteFilter(user?.id, user?.role?.id, treatmentClientOwnerId);
	const showDetails = canSeeLogbookDetails(user?.id, user?.role?.id, treatmentClientOwnerId);
	const showOrdersFilter = canSeeLogbookOrders(user?.role?.id);

	const [selectedItem, setSelectedItem] = useState<LogbookEntry | null>(null);

	const {
		timeline, filtersActivated, showFilters, setShowFilters,
		filterState: { serviceFilter, setServiceFilter, activeStatusFilter, setStatusFilter, showOrders, setShowOrders, taskFilter, setTaskFilter },
	} = useLogbookData(treatmentId, sessions, noteFilter);

	if (!treatmentId) return null;

	return (
		<Grid container sx={{ height: "calc(100vh - 300px)", backgroundColor: "#fff" }}>
			<Grid xs={showDetails ? 4 : 12} lg={showDetails ? 5 : 12} sx={{ position: "relative", height: "100%" }}>
				<Box ref={timelineScrollRef} sx={{ overflowY: "auto", height: "100%", p: 2 }}>
				{timeline.length === 0 ? (
					<Paper elevation={0} sx={{ p: 6, textAlign: "center", backgroundColor: "grey.50", borderRadius: 2, mt: 4 }}>
						<HistoryToggleOff sx={{ fontSize: 48, color: "text.disabled", mb: 1 }} />
						<Typography variant="subtitle1" color="text.secondary">Sem registos no diário de bordo.</Typography>
						<Typography variant="body2" color="text.disabled" sx={{ mt: 0.5 }}>As sessões e notas aparecerão aqui.</Typography>
					</Paper>
				) : (
					<>
					<Box sx={{ display: "flex", gap: 2, mb: 2, p: 2, alignItems: "center", justifyContent: "flex-end" }}>
						<Button
							startIcon={filtersActivated ? <FilterAlt /> : null}
							color="secondary"
							size="large"
							onClick={() => setShowFilters((prev) => !prev)}
						>
							{filtersActivated ? "Editar Filtros" : "Filtrar"}
						</Button>
					</Box>
					<Collapse in={showFilters}>
						<Box sx={{ display: "flex", gap: 1, mx: 2, mb: 1, alignItems: "center" }}>
							<LogbookServiceFilter
								sessions={sessions}
								selectedServiceIds={serviceFilter}
								onChange={setServiceFilter}
							/>
							<LogbookStatusFilter
								sessions={sessions}
								selectedStatuses={activeStatusFilter}
								onChange={setStatusFilter}
							/>
							{showOrdersFilter && <LogbookOrdersFilter showOrders={showOrders} onChange={setShowOrders} />}
							<LogbookTaskFilter value={taskFilter} onChange={setTaskFilter} />
						</Box>
					</Collapse>
					<Timeline
						sx={{
							[`& .${timelineItemClasses.root}:before`]: { flex: 0, padding: 0 },
							"& .MuiTimelineConnector-root": { width: "2px" },
						}}
					>
						{timeline.map((entry, index) => {
							const currentMonth = getEntryMonth(entry);
							const previousMonth = index > 0 ? getEntryMonth(timeline[index - 1]) : null;
							const monthHeader = currentMonth !== previousMonth
								? <MonthDivider month={currentMonth} isFirst={index === 0} />
								: null;
							const entryLinkUrl = !showDetails ? getEntryLinkUrl(entry, treatmentId) : undefined;

							if (entry.type === "prescription") {
								return (
									<Box key={`prescription-${entry.prescription.id}`}>
										{monthHeader}
										<LogbookPrescriptionItem
											prescription={entry.prescription}
											isSelected={selectedItem?.type === "prescription" && selectedItem.prescription.id === entry.prescription.id}
											onClick={() => setSelectedItem(entry)}
											linkUrl={entryLinkUrl}
										/>
									</Box>
								);
							}

							if (entry.type === "order") {
								return (
									<Box key={`order-${entry.order.id}-${entry.orderSubtype}`}>
										{monthHeader}
										<LogbookOrderItem
											order={entry.order}
											orderSubtype={entry.orderSubtype}
											entryDate={entry.entryDate}
											isSelected={selectedItem?.type === "order" && selectedItem.order.id === entry.order.id && selectedItem.orderSubtype === entry.orderSubtype}
											onClick={() => setSelectedItem(entry)}
											linkUrl={entryLinkUrl}
										/>
									</Box>
								);
							}

							if (entry.type === "note") {
								return (
									<Box key={`note-${entry.note.id}`}>
										{monthHeader}
										<LogbookNoteItemComponent
											note={entry.note}
											isMainTimeline
											isSelected={selectedItem?.type === "note" && selectedItem.note.id === entry.note.id}
											onClick={() => setSelectedItem(entry)}
											linkUrl={entryLinkUrl}
										/>
									</Box>
								);
							}

							if (entry.type === "task") {
								return (
									<Box key={`task-${entry.task.id}`}>
										{monthHeader}
										<LogbookTaskItemComponent
											task={entry.task}
											isSelected={selectedItem?.type === "task" && selectedItem.task.id === entry.task.id}
											onClick={() => setSelectedItem(entry)}
											linkUrl={entryLinkUrl}
										/>
									</Box>
								);
							}

							if (entry.type === "customer_survey") {
								return (
									<Box key={`survey-${entry.customerSurvey.id}`}>
										{monthHeader}
										<LogbookCustomerSurveyItemComponent
											customerSurvey={entry.customerSurvey}
											isSelected={selectedItem?.type === "customer_survey" && selectedItem.customerSurvey.id === entry.customerSurvey.id}
											onClick={() => setSelectedItem(entry)}
											linkUrl={entryLinkUrl}
										/>
									</Box>
								);
							}

							if (entry.type === "session") {
							return (
								<Box key={entry.session.id}>
									{monthHeader}
									<LogbookSessionItem
										session={entry.session}
										isSelected={selectedItem?.type === "session" && selectedItem.session.id === entry.session.id}
										onSelect={() => setSelectedItem(entry)}
										linkUrl={entryLinkUrl}
									/>
								</Box>
							);

							}
							return null
						})}
					</Timeline>
					</>

				)}
				</Box>
				<ScrollArrow visible={!isAtTop} onClick={scrollToTop} position="top" icon={<KeyboardArrowUp />} />
				<ScrollArrow visible={!isAtBottom} onClick={scrollToBottom} position="bottom" icon={<KeyboardArrowDown />} />
			</Grid>
			{showDetails && (
				<Grid key={selectedItem ? `${selectedItem.type}-${getEntryId(selectedItem)}` : "empty"} xs={8} lg={7} sx={{ borderLeft: 1, borderColor: "divider", overflowY: "auto", height: "100%", p: 3 }}>
					<LogbookDetailPanel selectedItem={selectedItem} treatmentId={treatmentId} />
				</Grid>
			)}
		</Grid>
	);
};

type ScrollArrowProps = {
	visible: boolean;
	onClick: () => void;
	position: "top" | "bottom";
	icon: ReactNode;
};

const ScrollArrow = ({ visible, onClick, position, icon }: ScrollArrowProps) => (
	<Fade in={visible}>
		<IconButton
			onClick={onClick}
			size="small"
			sx={{
				position: "absolute",
				[position]: 16,
				right: 16,
				zIndex: 10,
				backgroundColor: "background.paper",
				boxShadow: 2,
				"&:hover": { backgroundColor: "background.paper" },
			}}
		>
			{icon}
		</IconButton>
	</Fade>
);

type MonthDividerProps = {
	month: string;
	isFirst: boolean;
};

const MonthDivider = ({ month, isFirst }: MonthDividerProps) => (
	<Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1, mt: isFirst ? 0 : 2, px: 2 }}>
		<Divider sx={{ flexGrow: 1 }} />
		<Typography variant="caption" color="text.secondary" fontWeight="bold" sx={{ textTransform: "capitalize", whiteSpace: "nowrap" }}>
			{month}
		</Typography>
		<Divider sx={{ flexGrow: 1 }} />
	</Box>
);

const getEntryLinkUrl = (entry: LogbookEntry, treatmentId: string | number) => {
	if (entry.type === "session") return `/sessions/${entry.session.id}/detail`;
	if (entry.type === "note") {
		const notePath = entry.note.category === "psychology" ? "psychology" : "notes";
		return `/treatments/${treatmentId}/${notePath}?[noteId]=${entry.note.id}`;
	}
	if (entry.type === "order") return `/orders/${entry.order.id}`;
	if (entry.type === "task") return `/tasks/${entry.task.id}/detail`;
	if (entry.type === "customer_survey") return `/customer_surveys/${entry.customerSurvey.id}`;
	return undefined;
};
